import type { HttpErrorResponse } from '@angular/common/http';
import type { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import type { PropertyStatusService } from '../../../services/property-status/property-status.service';
import type { Product, PropertyStatus } from '../../../services/property-status/property-status.service.models';
import { PROPERTY_OVERVIEW_PAGE_SIZE } from './property-overview.models';
import type { PropertyOverviewOutcome } from './property-overview.models';

/** The token is valid but lacks the property-status consumer role, so retrying can never help. */
const DENIED_STATUSES: number[] = [401, 403];

/**
 * Feeds the property overview grid one server page at a time. The ~1200 properties are never held
 * client-side, so every page change is a fresh request rather than a slice of a cached list.
 */
export class PropertyOverviewDatasource implements IServerSideDatasource<PropertyStatus> {
  constructor(
    private readonly propertyStatusService: PropertyStatusService,
    private readonly reportOutcome: (outcome?: PropertyOverviewOutcome) => void,
    // Read per request rather than captured, so a filter change needs no new datasource.
    private readonly selectedProducts: () => Product[],
  ) {}

  private latestRequest = 0;

  getRows(params: IServerSideGetRowsParams<PropertyStatus>): void {
    const { startRow = 0, endRow } = params.request;
    // Both are typed optional, so a row span that isn't a real window falls back to the configured page.
    const size = endRow && endRow > startRow ? endRow - startRow : PROPERTY_OVERVIEW_PAGE_SIZE;

    const product = this.selectedProducts();
    const request = ++this.latestRequest;

    // Clearing the outcome drops the grid's overlay, which is what lets AG Grid's own loading one through.
    this.reportOutcome(undefined);

    this.propertyStatusService.getPropertyStatusList({ page: Math.floor(startRow / size), size, product }).subscribe({
      next: ({ items, total }) => {
        // A purged request keeps running, and AG Grid drops its rows against the dead cache — but what the
        // datasource reports itself has no such guard, so an overtaken response would outlive its filter.
        if (request === this.latestRequest) {
          this.reportOutcome({ status: 'loaded', total });
        }
        // rowCount is what gives the pager the real page count — without it it reads "Page 1 of 1".
        params.success({ rowData: items, rowCount: total });
      },
      error: (error: HttpErrorResponse) => {
        if (request === this.latestRequest) {
          this.reportOutcome({ status: 'failed', denied: DENIED_STATUSES.includes(error.status) });
        }
        params.fail();
      },
    });
  }
}
